/*
 *  -----------------------------------------------------------------------
 *    <copyright file="edit-template-organization-setup/index.tsx" company="Triumph Research Intelligence">
 *        Copyright (C) 2023 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { OrganizationReportTemplateVisibility } from '@interfaces/report-templates.interface';
import { orderBy } from '@progress/kendo-data-query';
import React, { useEffect, useState } from 'react';
import {
    EditTemplateOrganizationSetupInterface,
    OrganizationReportTemplateItem,
} from '@components/admin/edit-template-organization-setup/edit-template-organization-setup.interface';
import LibraryGrid from '@components/admin/library-grid';
import Button from '@components/core/buttons';
import { CustomDialog, DialogActions, DialogContent, DialogTitle } from '@components/core/dialog';
import DialogInformationBox from '@components/core/dialog-information-box/dialog-information-box';
import Loading from '@components/core/loading';
import { useGridCells, useTranslation } from '@hooks/index';
import NotificationService from '@utils/notification';
import {
    useGetOrganizationVisibilityForTemplate,
    useSetTemplateVisibleForOrganizations,
} from '@apis/bff-api/report-templates';

const EditTemplateOrganizationSetup = ({
    open,
    onClose,
    currentReportTemplate,
}: EditTemplateOrganizationSetupInterface) => {
    const { MaxLengthCell } = useGridCells();
    const { t } = useTranslation();
    const [selectedItems, setSelectedItems] = useState<OrganizationReportTemplateItem[]>([]);
    const [savedItems, setSavedItems] = useState<OrganizationReportTemplateItem[]>([]);
    const [orgsItemsData, setOrgsItemsData] = useState<OrganizationReportTemplateItem[]>([]);
    const { data, isLoading } = useGetOrganizationVisibilityForTemplate(currentReportTemplate?.id);
    const { trigger: postOrganizationReportTemplate, isMutating } = useSetTemplateVisibleForOrganizations();

    useEffect(() => {
        const orgsItemsArray = data?.map((item: OrganizationReportTemplateVisibility) => ({
            id: item.orgKey,
            name: item.name,
            visible: item.visible,
        }));
        const selectedCurrentItems = orgsItemsArray?.filter((item) => item.visible) || [];

        setSavedItems(selectedCurrentItems);
        setSelectedItems(selectedCurrentItems);
        if (!!orgsItemsArray?.length) setOrgsItemsData(orderBy(orgsItemsArray, [{ field: 'name', dir: 'asc' }]));
    }, [data]);

    const updateIds = () => {
        const organizationKeys = selectedItems.map((item: OrganizationReportTemplateItem) => item?.id) || [];

        const saveData = {
            templateId: currentReportTemplate?.id,
            organizationKeys,
        };

        postOrganizationReportTemplate(saveData)
            .then(() => {
                NotificationService.showNotification(
                    t('admin:organizationsTemplateSuccessfulUpdate', {
                        id: currentReportTemplate?._itemId || currentReportTemplate?.recordId,
                    }),
                );
                onClose();
            })
            .catch(() => {
                NotificationService.showNotification(t('errorMessage'));
            });
    };

    const hasChoiceNotChanged = () => {
        const selectedIds = selectedItems.map((item: OrganizationReportTemplateItem) => item?.id);
        const savedIds = savedItems.map((item: OrganizationReportTemplateItem) => item?.id);

        return (
            selectedIds?.length === savedIds?.length && selectedIds.every((value, index) => savedIds.includes(value))
        );
    };

    return (
        <CustomDialog
            onClose={close}
            fullWidth
            scroll="paper"
            maxWidth="md"
            open={open}
            data-testid="edit-template-org-dialog"
        >
            <DialogTitle id="edit-template-org-title">
                {t('admin:editTemplateOrganizationSetup')}
                <FontAwesomeIcon
                    icon={['fal', 'times']}
                    className="fa-lg close-icon-cv"
                    onClick={() => onClose()}
                    data-testid="close-dialog"
                />
            </DialogTitle>
            <DialogContent className="edit-template-org-dialog-content pb-0 mb-20">
                {(isLoading || isMutating) && <Loading float />}
                <div className="info-box-wrapper">
                    <DialogInformationBox topText={t('admin:editTemplateOrganizationSetupInfoBoxText')} />
                </div>
                <LibraryGrid
                    items={orgsItemsData}
                    columns={[
                        {
                            title: t('organisation'),
                            id: 'item-org',
                            field: 'name',
                            cells: { data: MaxLengthCell(50) },
                        },
                    ]}
                    selection={{
                        selectedItems,
                        setSelectedItems,
                        showItemsCount: true,
                    }}
                    itemsType={t('admin:organizationRecord(s)')}
                    hasChecked
                />
            </DialogContent>
            <DialogActions className="!k-pr-6 !k-pl-6 k-mb-6">
                <Button
                    text={t('update')}
                    id="edit-template-org-save"
                    data-testid="submit-edit-template-org"
                    onClick={() => updateIds()}
                    disabled={hasChoiceNotChanged() || isMutating}
                />
            </DialogActions>
        </CustomDialog>
    );
};

export default EditTemplateOrganizationSetup;
